import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Activity, ArrowLeft, Loader2, Mail, RefreshCw, Send, ShieldCheck, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { sendTestPaymentEmail } from "@/lib/email/test-payment-email.functions";
import { sendTestSessionEmail } from "@/lib/email/test-session-email.functions";
import { previewEmailTemplate } from "@/lib/email/preview-template.functions";
import {

  listSandboxActivity,
  sendTestAuthEmail,
  sendTestWaitlistEmail,
} from "@/lib/email/sandbox.functions";
import { SANDBOX_RECIPIENT } from "@/lib/email/sandbox-config";

export const Route = createFileRoute("/admin/testing")({
  head: () => ({
    meta: [{ title: "Email Sandbox · Admin · WeLivdIt" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: AdminTestingPage,
});

type AudienceKey = "group2" | "group1" | "all";

function AdminTestingPage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [me, setMe] = useState<string>("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      const email = data.user?.email ?? "";
      setMe(email);
      setAuthChecked(true);
    })();
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-5 py-10 space-y-10">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <h1 className="mt-4 font-display text-3xl font-semibold">Email Sandbox</h1>
        <p className="mt-1 text-sm text-foreground/70">
          Send real test emails and preview templates before broadcasting to students.
        </p>
      </div>

      <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-800 dark:text-emerald-300">
        🟢 Managed Delivery Sandbox Active — All test sends route via Lovable Delivery. Test recipients are locked to{" "}
        <span className="font-mono">{SANDBOX_RECIPIENT}</span> unless another @welivdit.com inbox is entered.
      </div>

      <PaymentTestCard defaultEmail={me || SANDBOX_RECIPIENT} />
      <WaitlistTestCard defaultEmail={me || SANDBOX_RECIPIENT} />
      <SessionTestCard defaultEmail={me || SANDBOX_RECIPIENT} />
      <AuthTestCard defaultEmail={me || SANDBOX_RECIPIENT} />
      <ActivityFeedCard />
    </section>
  );
}

/* --------------------------------- waitlist ---------------------------------- */

function WaitlistTestCard({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState("Test Student");
  const [sessionTitle, setSessionTitle] = useState("Free Live F1 Visa Prep Session");
  const [sending, setSending] = useState(false);
  const send$ = useServerFn(sendTestWaitlistEmail);

  const send = async () => {
    setSending(true);
    try {
      const r = await send$({ data: { email, name, sessionTitle } });
      if (r.sent) toast.success(`Waitlist confirmation sent to ${r.recipient}`);
      else toast.error("Send failed", { description: (r as any).reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h2 className="font-semibold">Waitlist / registration confirmation</h2>
      </div>
      <p className="mt-1 text-xs text-foreground/60">
        Sends the welcome email a student receives after joining a free session waitlist.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Target email (internal only)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={SANDBOX_RECIPIENT}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Student name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-xs font-medium text-foreground/70">Session title</span>
          <input
            type="text"
            value={sessionTitle}
            onChange={(e) => setSessionTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send test waitlist confirmation
        </button>
        <PreviewButton
          templateName="waitlist-confirmation"
          templateData={{ name, sessionTitle }}
        />
      </div>
    </div>
  );
}

/* ----------------------------------- auth ------------------------------------ */

const AUTH_KINDS = [
  { key: "auth-magic-link", label: "Magic link / OTP login" },
  { key: "auth-recovery", label: "Password reset" },
  { key: "auth-signup", label: "Signup confirmation" },
] as const;

function AuthTestCard({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [kind, setKind] = useState<(typeof AUTH_KINDS)[number]["key"]>("auth-magic-link");
  const [sending, setSending] = useState(false);
  const send$ = useServerFn(sendTestAuthEmail);

  const send = async () => {
    setSending(true);
    try {
      const r = await send$({ data: { email, kind } });
      if (r.sent) toast.success(`Auth test email sent to ${r.recipient}`);
      else toast.error("Send failed", { description: (r as any).reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h2 className="font-semibold">Auth emails (magic link · reset · OTP)</h2>
      </div>
      <p className="mt-1 text-xs text-foreground/60">
        Renders the live authentication templates with a dummy link — nothing in the auth system is changed.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Target email (internal only)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={SANDBOX_RECIPIENT}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Auth email type</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as any)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            {AUTH_KINDS.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send auth test email
        </button>
        <PreviewButton
          templateName={kind}
          templateData={{ siteName: "WeLivdIt", confirmationUrl: "https://welivdit.com/auth" }}
        />
      </div>
    </div>
  );
}

/* -------------------------------- activity feed ------------------------------- */

function statusTone(status: string) {
  if (status === "sent") return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  if (status === "failed" || status === "bounced" || status === "complained")
    return "bg-red-500/10 text-red-700 dark:text-red-300";
  if (status === "suppressed") return "bg-amber-500/10 text-amber-700 dark:text-amber-300";
  return "bg-muted text-foreground/70";
}

function ActivityFeedCard() {
  const [loading, setLoading] = useState(true);
  const [sends, setSends] = useState<any[]>([]);
  const [suppressions, setSuppressions] = useState<any[]>([]);
  const load$ = useServerFn(listSandboxActivity);

  const load = async () => {
    setLoading(true);
    try {
      const r = await load$({ data: undefined as any });
      setSends(r.sends);
      setSuppressions(r.suppressions);
    } catch (e: any) {
      toast.error("Could not load activity", { description: e?.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <h2 className="font-semibold">Delivery activity &amp; suppression list</h2>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-accent"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-foreground/60">
            <tr>
              <th className="py-2 pr-3 font-medium">When</th>
              <th className="py-2 pr-3 font-medium">Template</th>
              <th className="py-2 pr-3 font-medium">Recipient</th>
              <th className="py-2 pr-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {sends.map((s) => (
              <tr key={s.id} className="border-t border-border/60">
                <td className="py-2 pr-3 whitespace-nowrap text-foreground/70">
                  {new Date(s.created_at).toLocaleString()}
                </td>
                <td className="py-2 pr-3">{s.template_name}</td>
                <td className="py-2 pr-3 font-mono text-[11px]">{s.recipient_email}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusTone(s.status)}`}>
                    {s.status}
                  </span>
                  {s.error_message && (
                    <span className="ml-2 text-[11px] text-foreground/50">{String(s.error_message).slice(0, 80)}</span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && sends.length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-center text-foreground/50">
                  No email activity recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
          Suppressed recipients (bounces · complaints · unsubscribes)
        </h3>
        <ul className="mt-3 space-y-2">
          {suppressions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono">{s.email}</span>
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-300">
                {s.reason}
              </span>
              <span className="text-foreground/50">{new Date(s.created_at).toLocaleDateString()}</span>
            </li>
          ))}
          {!loading && suppressions.length === 0 && (
            <li className="text-xs text-foreground/50">No suppressed recipients.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* --------------------------------- payment ---------------------------------- */

function PaymentTestCard({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState("Test Student");
  const [plan, setPlan] = useState<"basic_support" | "full_support" | "refusal_support">("full_support");
  const [sending, setSending] = useState(false);
  const send$ = useServerFn(sendTestPaymentEmail);

  useEffect(() => {
    if (defaultEmail && !email) setEmail(defaultEmail);
  }, [defaultEmail, email]);

  const previewData = useMemo(
    () => ({
      name,
      planKey: plan,
      planName:
        plan === "basic_support" ? "Basic Support" : plan === "refusal_support" ? "Refusal Support" : "Full Support",
      amountInr: plan === "basic_support" ? 3000 : plan === "refusal_support" ? 7000 : 4500,
      dashboardUrl: "https://welivdit.com/student-dashboard",
    }),
    [name, plan],
  );

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid target email");
      return;
    }
    setSending(true);
    try {
      const r = await send$({ data: { email, name, planKey: plan } });
      if (r.sent) toast.success(`Test payment email sent to ${email}`);
      else toast.error("Send failed", { description: r.reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h2 className="font-semibold">Payment test emails</h2>
      </div>
      <p className="mt-1 text-xs text-foreground/60">Sends a mock payment confirmation via the transactional queue.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Target email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Buyer name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-xs font-medium text-foreground/70">Plan tier</span>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value as any)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="basic_support">Basic Support (₹3,000)</option>
            <option value="full_support">Full Support (₹4,500)</option>
            <option value="refusal_support">Refusal Support (₹7,000)</option>
          </select>
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send test payment receipt
        </button>
        <PreviewButton templateName="payment-confirmation" templateData={previewData} />
      </div>
    </div>
  );
}

/* --------------------------------- session broadcast ---------------------------------- */

function SessionTestCard({ defaultEmail }: { defaultEmail: string }) {
  const [testEmail, setTestEmail] = useState(defaultEmail);
  const [audience, setAudience] = useState<AudienceKey>("group2");
  const [sessionTitle, setSessionTitle] = useState("Clarification: No Live Session Scheduled Today");
  const [dateLabel, setDateLabel] = useState("29th July 2026");
  const [timeLabel, setTimeLabel] = useState("7:00 PM IST");
  const [description, setDescription] = useState(
    "Hi there, please note there is NO live Free Visa session scheduled for today. Earlier automated test emails were sent inadvertently during site internal maintenance. We sincerely apologize for any confusion!\n\nOur next upcoming live session will be held on 29th July. Stay tuned!\n\nThank you and sorry for the inconvenience,\nTeam WeLivdIt",
  );
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const send$ = useServerFn(sendTestSessionEmail);

  useEffect(() => {
    if (defaultEmail && !testEmail) setTestEmail(defaultEmail);
  }, [defaultEmail, testEmail]);

  const previewData = useMemo(
    () => ({
      name: "Student",
      sessionTitle,
      dateLabel,
      timeLabel,
      description,
      hostName: "Team WeLivdIt",
      registrationUrl: "https://welivdit.com",
    }),
    [sessionTitle, dateLabel, timeLabel, description],
  );

  // Function 1: Send single test email to testEmail inbox
  const handleSendTestToSelf = async () => {
    const target = testEmail.trim() || defaultEmail;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Enter a valid test email address");
      return;
    }
    if (!sessionTitle.trim()) {
      toast.error("Session title is required");
      return;
    }
    setSendingTest(true);
    try {
      const r = await send$({
        data: {
          email: target,
          templateName: audience === "group2" ? "session-vip" : "session-announcement",
          sessionTitle,
          dateLabel: dateLabel || "Announcement",
          timeLabel: timeLabel || "Immediate",
          hostName: "Team WeLivdIt",
          registrationUrl: "https://welivdit.com",
        },
      });
      if (r.sent) toast.success(`Test email sent to ${target}`);
      else toast.error("Send failed", { description: (r as any).reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSendingTest(false);
    }
  };

  // Function 2: Send broadcast to selected audience group
  const handleSendBroadcast = async () => {
    if (!sessionTitle.trim()) {
      toast.error("Session title is required");
      return;
    }
    setSendingBroadcast(true);
    try {
      const r = await send$({
        data: {
          email: defaultEmail,
          templateName: "session-announcement",
          sessionTitle,
          dateLabel: dateLabel || "Announcement",
          timeLabel: timeLabel || "Immediate",
          hostName: "Team WeLivdIt",
          registrationUrl: "https://welivdit.com",
        },
      });
      if (r.sent) toast.success("Broadcast message successfully queued!");
      else toast.error("Send failed", { description: (r as any).reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSendingBroadcast(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-primary" />
        <h2 className="font-semibold text-base">Broadcast / Session Email</h2>
      </div>
      <p className="text-xs text-foreground/60">
        Send a test email to yourself first, then broadcast to selected audience groups.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Test Target Email (Optional for self-test)</span>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="sriramalla@welivdit.com"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Audience / Target Group</span>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value as AudienceKey)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="group2">Group 2 · Paid customer VIP email (90+ Enrolled)</option>
            <option value="group1">Group 1 · Waitlist queue lead email</option>
            <option value="all">Group 3 · All Registered Students & Waitlist</option>
          </select>
        </label>

        <label className="text-sm sm:col-span-2">
          <span className="text-xs font-medium text-foreground/70">Session Title / Subject *</span>
          <input
            type="text"
            value={sessionTitle}
            onChange={(e) => setSessionTitle(e.target.value)}
            placeholder="Clarification: No Live Session Scheduled Today"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Date Label (Optional)</span>
          <input
            type="text"
            value={dateLabel}
            onChange={(e) => setDateLabel(e.target.value)}
            placeholder="29th July 2026"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm">
          <span className="text-xs font-medium text-foreground/70">Time Label (Optional)</span>
          <input
            type="text"
            value={timeLabel}
            onChange={(e) => setTimeLabel(e.target.value)}
            placeholder="7:00 PM IST"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm sm:col-span-2">
          <span className="text-xs font-medium text-foreground/70">Description / Message Body *</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="Hi there, please note there is NO live session scheduled for today..."
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleSendTestToSelf}
          disabled={sendingTest || sendingBroadcast}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-background text-sm font-semibold hover:bg-accent transition disabled:opacity-60"
        >
          {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Test Email (Just Me)
        </button>

        <button
          type="button"
          onClick={handleSendBroadcast}
          disabled={sendingTest || sendingBroadcast}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
        >
          {sendingBroadcast ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Broadcast to {audience === "group2" ? "Paid Members" : audience === "group1" ? "Waitlist" : "All Users"}
        </button>

        <PreviewButton templateName="session-announcement" templateData={previewData} />
      </div>
    </div>
  );
}

/* --------------------------------- preview ---------------------------------- */

function PreviewButton({ templateName, templateData }: { templateName: string; templateData: Record<string, any> }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const preview$ = useServerFn(previewEmailTemplate);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await preview$({ data: { templateName, templateData } });
      setHtml(r.html);
      setSubject(r.subject);
    } catch (e: any) {
      toast.error("Preview failed", { description: e?.message ?? String(e) });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-background text-sm font-semibold hover:bg-accent"
      >
        <Eye className="h-4 w-4" />
        Preview email template
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-3xl max-h-[90vh] bg-background border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-border p-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-foreground/60">Subject preview</p>
                <p className="font-semibold truncate">{subject || templateName}</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-accent" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden bg-muted/30">
              {loading ? (
                <div className="h-full grid place-items-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <iframe srcDoc={html} title="Email preview" className="w-full h-[75vh] bg-white" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
